import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState } from 'react';

// MUI
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

// Actions
import { openSmallModal } from '@actions/modal';

// Common
import { Asterisk } from '@commons/Asterisk';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { GenericCancelButton, GenericDownloadButton } from '@commons/Buttons';
import { getUserTimezone } from '@commons/utils/date';
import { SingleDatePicker } from '@commons/DatePickers/SingleDatePicker';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

// Design
import {
  Container,
  ContentContainer,
  FooterContainer,
  HeaderContainer,
  Section,
  Title,
} from '../PdfConfig/styledComponents';

import PdfGenerationModal from '../PdfGenerationModal';

/**
 * @param {{
 *  categories: object[]
 *  onClose: () => void
 *  storeId: string
 * }} props
 *
 * @return {JSX.Element}
 */
const DetailedPdfConfig = (props) => {
  const USER_TIMEZONE = getUserTimezone();
  const TODAY = moment.tz(USER_TIMEZONE);

  const {
    categories,
    storeId,
    onClose,
    // From redux
    openSmallModal,
  } = props;

  const [selectedDate, setSelectedDate] = useState(TODAY);

  const [includeProducts, setIncludeProducts] = useState(true);
  const [includeProductsComposition, setIncludeProductsComposition] = useState(false);
  const [includeRecipes, setIncludeRecipes] = useState(false);
  const [includeRecipesComposition, setIncludeRecipesComposition] = useState(false);
  const [includeIngredients, setIncludeIngredients] = useState(false);

  const handleProductsCheck = (event) => {
    setIncludeProducts(event.target.checked);

    if (!event.target.checked) {
      setIncludeProductsComposition(false);
    }
  };

  const handleRecipesCheck = (event) => {
    setIncludeRecipes(event.target.checked);

    if (!event.target.checked) {
      setIncludeRecipesComposition(false);
    }
  };

  const generateSalesPlanningPdf = () => {
    const payload = {
      categoryIds: categories.map(({ id }) => id),
      productionDate: moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      planningConfiguration: {
        includeProducts,
        includeProductsComposition,
        includeRecipes,
        includeRecipesComposition,
        includeIngredients,
      },
    };

    openSmallModal({ component: PdfGenerationModal, storeId, payload });

    onClose();
  };

  const isDownloadDisabled = !includeProducts && !includeRecipes && !includeIngredients;

  return (
    <Container>
      <HeaderContainer>
        <Title>{i18next.t('FORECAST.SALES_PLANNING.DETAILED_PDF_CONFIG_TITLE')}</Title>
      </HeaderContainer>

      <ContentContainer>
        <Section>
          <Text color={ENUM_COLORS.IP_BLACK_1}>
            <Asterisk />
            {i18next.t('FORECAST.SALES_PLANNING.PDF_PRODUCTION_DATE')} :
          </Text>
          <SingleDatePicker
            customStyle={{ position: 'inherit' }}
            date={selectedDate}
            maxPastDate={TODAY}
            onDateChange={setSelectedDate}
          />
        </Section>

        <Section>
          <Text color={ENUM_COLORS.IP_BLACK_1}>
            <Asterisk />
            {i18next.t('FORECAST.SALES_PLANNING.PDF_TYPE')} :
          </Text>
          <FormGroup sx={{ paddingLeft: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox checked={includeProducts} size="large" onChange={handleProductsCheck} />
              }
              label={
                <Text color={ENUM_COLORS.IP_BLACK_1} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
                  {i18next.t('FORECAST.SALES_PLANNING.PDF_PRODUCTS')}
                </Text>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeProductsComposition}
                  disabled={!includeProducts}
                  size="large"
                  onChange={(event) => setIncludeProductsComposition(event.target.checked)}
                />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_INCLUDE_COMPOSITION')}
              style={{ marginLeft: '24px' }}
            />
          </FormGroup>

          <FormGroup sx={{ paddingLeft: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox checked={includeRecipes} size="large" onChange={handleRecipesCheck} />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_RECIPES')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeRecipesComposition}
                  disabled={!includeRecipes}
                  size="large"
                  onChange={(event) => setIncludeRecipesComposition(event.target.checked)}
                />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_INCLUDE_COMPOSITION')}
              style={{ marginLeft: '24px' }}
            />
          </FormGroup>

          <FormGroup sx={{ paddingLeft: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeIngredients}
                  size="large"
                  onChange={(event) => setIncludeIngredients(event.target.checked)}
                />
              }
              label={i18next.t('FORECAST.SALES_PLANNING.PDF_INGREDIENTS')}
            />
          </FormGroup>
        </Section>
      </ContentContainer>

      <FooterContainer>
        <GenericCancelButton handleClick={onClose} />
        <GenericDownloadButton
          handleClick={generateSalesPlanningPdf}
          isDisabled={isDownloadDisabled}
        />
      </FooterContainer>
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  openSmallModal: (params) => {
    dispatch(openSmallModal(params));
  },
});

export default connect(null, mapDispatchToProps)(DetailedPdfConfig);
